import React, { useState, useEffect } from 'react';
import sharepointService from '../services/sharepointService';
import CreateFolderModal from './CreateFolderModal';
import { formatTimestamp } from '../utils/dateUtils';

/**
 * Modal component for accepting a file and pushing it to SharePoint
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Function to close the modal
 * @param {object} file - The file object to accept
 * @param {string} clientName - The client name
 * @param {string} clientId - The client ID
 * @param {function} onAccept - Callback function when file is accepted
 * @param {boolean} isApprovingAll - Whether to approve filename, push, and approve summary in one operation
 * @param {string} suggestedFolder - AI-suggested folder path to pre-select
 * @param {string} folderConfidence - Confidence level of the folder suggestion (high, medium, low)
 */
export default function AcceptFileModal({
    isOpen,
    onClose,
    file,
    clientName,
    knowledgebaseClientId,
    onAccept,
    isApprovingAll = false,
    suggestedFolder = '',
    folderConfidence = '',
    docType = '',
    subType = '',
    accountNumber = '',
    fileExistsInSharePoint = false,
    uploadOriginal = false
}) {
    const [folders, setFolders] = useState([]);
    const [selectedFolderId, setSelectedFolderId] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [isAccepting, setIsAccepting] = useState(false);
    const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
    const [folderAutoSelected, setFolderAutoSelected] = useState(false);
    const [overwriteExisting, setOverwriteExisting] = useState(false);

    // Determine the client folder path based on client name's first letter
    const getClientBasePath = () => {
        if (!clientName) return null;
        const firstChar = clientName.trim().toUpperCase().charAt(0);
        let letterRange = '';
        if (firstChar >= 'A' && firstChar <= 'F') {
            letterRange = 'Clients A - F';
        } else if (firstChar >= 'G' && firstChar <= 'K') {
            letterRange = 'Clients G - K';
        } else if (firstChar >= 'L' && firstChar <= 'Q') {
            letterRange = 'Clients L - Q';
        } else if (firstChar >= 'R' && firstChar <= 'Z') {
            letterRange = 'Clients R - Z';
        } else {
            // Non-alphabetic first character, can't determine range
            return null;
        }
        return `Client Documents/${letterRange}/${clientName.trim()}`;
    };

    // Suggested folders are relative to the client folder (e.g. "FP Docs/Managed Statements").
    // Without the client prefix they resolve to the SharePoint drive root.
    const toClientAbsolutePath = (relativePath) => {
        const cleaned = (relativePath || '').replace(/\\/g, '/').replace(/^\/+|\/+$/g, '').trim();
        if (!cleaned) return '';
        if (cleaned.toLowerCase().startsWith('client documents/')) return cleaned;
        const base = getClientBasePath();
        return base ? `${base}/${cleaned}` : cleaned;
    };

    // Load folders when modal opens
    useEffect(() => {
        if (isOpen && clientName) {
            loadFolders();
        }
    }, [isOpen, clientName]);

    // Reset state when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setSelectedFolderId('');
            setError('');
            setIsAccepting(false);
            setShowCreateFolderModal(false);
            setFolderAutoSelected(false);
            setOverwriteExisting(false);
        }
    }, [isOpen]);

    // Auto-select suggested folder when folders are loaded
    useEffect(() => {
        if (folders.length > 0 && suggestedFolder && !folderAutoSelected) {
            // Normalize the suggested folder path (replace backslashes with forward slashes)
            const normalizedSuggestion = suggestedFolder.replace(/\\/g, '/').toLowerCase().trim();
            
            // Create a fuzzy version that handles common singular/plural variations
            const createFuzzyPath = (path) => {
                return path
                    .replace(/investments/gi, 'investment')  // Normalize to singular
                    .replace(/documents/gi, 'document')
                    .replace(/accounts/gi, 'account')
                    .replace(/\s+/g, ' ')  // Normalize whitespace
                    .trim();
            };
            const fuzzyNormalizedSuggestion = createFuzzyPath(normalizedSuggestion);
            
            console.log('Looking for folder match:', { 
                suggestedFolder, 
                normalizedSuggestion,
                fuzzyNormalizedSuggestion,
                folders: folders.map(f => ({ 
                    name: f.name, 
                    path: f.path, 
                    full_path: f.full_path,
                    normalized_full_path: (f.full_path || '').replace(/\\/g, '/').toLowerCase().trim()
                })) 
            });
            
            // Try to find a folder that matches the suggested folder path
            const matchingFolder = folders.find(folder => {
                // Normalize all folder path variations to forward slashes
                const folderFullPath = (folder.full_path || '').replace(/\\/g, '/').toLowerCase().trim();
                const folderPath = (folder.path || '').replace(/\\/g, '/').toLowerCase().trim();
                const folderName = (folder.name || '').replace(/\\/g, '/').toLowerCase().trim();
                
                // Create fuzzy versions for comparison
                const fuzzyFolderFullPath = createFuzzyPath(folderFullPath);
                const fuzzyFolderPath = createFuzzyPath(folderPath);
                const fuzzyFolderName = createFuzzyPath(folderName);
                
                // Extract relative path from full_path by finding the client folder pattern and taking what comes after
                // Full path is like: "Client Documents/Clients R - Z/Schmitz, Greg/FP Docs/Investment/Non-Mgd"
                // We want to extract: "FP Docs/Investment/Non-Mgd"
                let relativePath = folderFullPath;
                const clientFolderMatch = folderFullPath.match(/client documents\/clients [a-z] - [a-z]\/[^/]+\//i);
                if (clientFolderMatch) {
                    relativePath = folderFullPath.substring(clientFolderMatch[0].length);
                }
                const fuzzyRelativePath = createFuzzyPath(relativePath);
                
                const matches = {
                    // Exact matches
                    condition1: folderFullPath.endsWith('/' + normalizedSuggestion),
                    condition2: folderFullPath === normalizedSuggestion,
                    condition3: folderPath === normalizedSuggestion,
                    condition4: folderName === normalizedSuggestion,
                    condition5: folderName === normalizedSuggestion.split('/').pop(),
                    condition6: relativePath === normalizedSuggestion,
                    condition7: relativePath.endsWith(normalizedSuggestion),
                    // Fuzzy matches (handles singular/plural variations)
                    condition8: fuzzyFolderFullPath.endsWith('/' + fuzzyNormalizedSuggestion),
                    condition9: fuzzyFolderFullPath === fuzzyNormalizedSuggestion,
                    condition10: fuzzyFolderPath === fuzzyNormalizedSuggestion,
                    condition11: fuzzyRelativePath === fuzzyNormalizedSuggestion,
                    condition12: fuzzyRelativePath.endsWith(fuzzyNormalizedSuggestion),
                };
                
                const isMatch = Object.values(matches).some(v => v);
                if (isMatch) {
                    console.log('Found match:', { folderName, folderPath, folderFullPath, relativePath, matches });
                }
                
                return isMatch;
            });

            if (matchingFolder) {
                setSelectedFolderId(matchingFolder.id);
                setFolderAutoSelected(true);
                console.log(`Auto-selected folder "${matchingFolder.name}" based on AI suggestion "${suggestedFolder}"`);
            } else {
                // AI suggested a folder that doesn't exist - add it as a new option
                // Normalize the path for consistency
                const normalizedPath = suggestedFolder.replace(/\\/g, '/');
                // Suggestions are relative to the client folder, so anchor them there
                const absolutePath = toClientAbsolutePath(normalizedPath);
                const newFolderEntry = {
                    id: `ai-suggested-${normalizedPath}`, // Synthetic ID to indicate it needs to be created
                    name: `${normalizedPath} (Create New)`,
                    full_path: absolutePath,
                    path: absolutePath,
                    isAiSuggested: true, // Flag to indicate this folder needs to be created
                    needsCreation: true,
                };
                setFolders(prevFolders => [newFolderEntry, ...prevFolders]);
                setSelectedFolderId(newFolderEntry.id);
                setFolderAutoSelected(true);
                console.log(`Added AI-suggested folder "${normalizedPath}" to folder list (will be created on accept)`);
            }
        } else if (folders.length > 0 && !selectedFolderId) {
            // No suggestion, just select the first folder
            setSelectedFolderId(folders[0].id);
        }
    }, [folders, suggestedFolder, folderAutoSelected, selectedFolderId]);

    const loadFolders = async () => {
        if (!clientName) return;

        setLoading(true);
        setError('');

        try {
            // Use the calculated base path to skip searching for the client folder
            const basePath = getClientBasePath();
            
            // Load both existing folders and predefined folder options in parallel
            const [existingResponse, predefinedResponse] = await Promise.all([
                sharepointService.getClientFolders(clientName, basePath),
                sharepointService.getBackfillFolderOptions()
            ]);
            
            const rawExistingFolders = existingResponse.folders || [];
            const predefinedFolders = predefinedResponse.folders || [];
            
            // The clientBasePath is like "Client Documents/Clients R - Z/Test, Me"
            const clientBasePath = basePath || `Client Documents/${clientName.trim()}`;
            
            // Filter out nested duplicate folders from existing folders
            // Some folders have incorrect paths where the client path is duplicated
            // e.g., "Client Documents/Clients R - Z/Test, Me/Client Documents/Clients R - Z/Test, Me/FP Docs"
            // We want to keep only folders where full_path starts with clientBasePath but doesn't contain it again
            const existingFolders = rawExistingFolders.filter(f => {
                const fullPath = f.full_path || '';
                // Check if the client path appears more than once (nested duplicate)
                if (fullPath.startsWith(clientBasePath)) {
                    const restOfPath = fullPath.substring(clientBasePath.length);
                    // If the rest of the path contains the client path again, it's a duplicate
                    if (restOfPath.includes(clientBasePath) || restOfPath.includes('/Client Documents/')) {
                        return false;
                    }
                }
                return true;
            });
            
            // Create a set of existing folder RELATIVE paths for deduplication
            // We need to extract the relative path from full_path (strip the clientBasePath prefix)
            const existingRelativePaths = new Set(
                existingFolders.map(f => {
                    const fullPath = f.full_path || f.path || f.name || '';
                    if (fullPath.startsWith(clientBasePath + '/')) {
                        return fullPath.substring(clientBasePath.length + 1); // Strip clientBasePath/ prefix
                    } else if (fullPath === clientBasePath) {
                        return '/'; // Root folder
                    }
                    return fullPath; // Return as-is if doesn't match expected pattern
                })
            );
            
            // Add predefined folders that don't already exist
            const additionalFolders = predefinedFolders
                .filter(pf => {
                    // Skip root since we already have it
                    if (pf.path === '/' || pf.id === 'root') return false;
                    // Check if this predefined folder already exists (by relative path)
                    return !existingRelativePaths.has(pf.path) && !existingRelativePaths.has(pf.name);
                })
                .map(pf => ({
                    id: `predefined_${pf.id}`,
                    name: pf.name,
                    full_path: `${clientBasePath}/${pf.path}`,
                    path: pf.path,
                    description: pf.description,
                    isPredefined: true // Mark as predefined so we know to create it
                }));
            
            // Combine existing folders first, then predefined options
            const allFolders = [...existingFolders, ...additionalFolders];
            
            // Sort folders: root first, then alphabetically by name
            allFolders.sort((a, b) => {
                // Root folder always first
                if (a.id === 'root') return -1;
                if (b.id === 'root') return 1;
                // Then sort alphabetically by name
                const nameA = (a.name || '').toLowerCase();
                const nameB = (b.name || '').toLowerCase();
                return nameA.localeCompare(nameB);
            });
            
            setFolders(allFolders);
        } catch (err) {
            console.error('Error loading folders:', err);
            setError('Failed to load folders. Please try again.');
            setFolders([]);
        } finally {
            setLoading(false);
        }
    };

    const handleAccept = async () => {
        if (!file || !clientName || !selectedFolderId) {
            setError('Please select a folder before accepting.');
            return;
        }

        // Find the selected folder to get its full path
        let selectedFolder = folders.find(f => f.id === selectedFolderId);
        if (!selectedFolder) {
            setError('Selected folder not found. Please try again.');
            return;
        }

        if (uploadOriginal && !window.confirm(
            'Upload the original, unredacted version to SharePoint? ' +
            'Sensitive information will not be redacted.'
        )) {
            return;
        }

        setIsAccepting(true);
        setError('');

        try {
            // If this is an AI-suggested folder that needs to be created, create it first
            if (selectedFolder.needsCreation) {
                // Resolve the suggested folder to an absolute path under the client folder
                const normalizedPath = toClientAbsolutePath(suggestedFolder);
                const clientBasePath = getClientBasePath();

                if (!clientBasePath && !normalizedPath.toLowerCase().startsWith('client documents/')) {
                    setError(`Could not determine the SharePoint folder for client "${clientName}". Please select an existing folder instead.`);
                    setIsAccepting(false);
                    return;
                }

                // Only create the levels below the client folder - the client folder already exists
                const rootPath = normalizedPath.toLowerCase().startsWith(`${(clientBasePath || '').toLowerCase()}/`)
                    ? clientBasePath
                    : '';
                const folderParts = (rootPath ? normalizedPath.substring(rootPath.length) : normalizedPath)
                    .split('/')
                    .filter(part => part.trim());
                
                console.log(`Creating AI-suggested folder path: ${normalizedPath} in client ${clientName}`);
                
                try {
                    let parentFolderPath = rootPath;
                    let lastCreatedFolder = null;
                    
                    // Create each folder level sequentially, using returnExisting=true to get folder ID even if it exists
                    for (const folderName of folderParts) {
                        console.log(`Creating folder: ${folderName} at parent path: ${parentFolderPath || '(root)'}`);
                        const createResponse = await sharepointService.createFolder(
                            folderName,
                            parentFolderPath,
                            clientName,
                            true // returnExisting: get existing folder info if it already exists
                        );
                        
                        if (createResponse.success && createResponse.folder) {
                            lastCreatedFolder = createResponse.folder;
                            // Update parent path for next iteration
                            parentFolderPath = createResponse.folder.full_path || 
                                              (parentFolderPath ? `${parentFolderPath}/${folderName}` : folderName);
                            const status = createResponse.already_existed ? 'already exists' : 'created';
                            console.log(`Folder ${folderName} ${status} with ID: ${createResponse.folder.id}, path: ${parentFolderPath}`);
                        } else {
                            throw new Error(createResponse.error || 'Failed to create folder');
                        }
                    }
                    
                    // Update selectedFolder with the final (deepest) folder's info
                    if (lastCreatedFolder) {
                        selectedFolder = {
                            ...selectedFolder,
                            id: lastCreatedFolder.id,
                            full_path: lastCreatedFolder.full_path || lastCreatedFolder.path || normalizedPath,
                            needsCreation: false,
                        };
                    } else {
                        // Shouldn't happen with returnExisting=true, but handle gracefully
                        selectedFolder = {
                            ...selectedFolder,
                            full_path: parentFolderPath || normalizedPath,
                            needsCreation: false,
                        };
                        console.log('Using path without folder ID:', parentFolderPath);
                    }
                } catch (createErr) {
                    console.error('Error creating AI-suggested folder:', createErr);
                    setError(`Failed to create folder "${selectedFolder.full_path}": ${createErr.message}`);
                    setIsAccepting(false);
                    return;
                }
            }

            let response;
            
            // If approving all, use the combined endpoint that approves filename, pushes, and approves summary
            if (isApprovingAll) {
                response = await sharepointService.approveFileAndSummary(
                    file.fileId,
                    clientName,
                    knowledgebaseClientId,
                    selectedFolder.id,
                    selectedFolder.full_path || selectedFolder.path || '',
                    overwriteExisting, // Use checkbox state for overwrite
                    docType,
                    subType,
                    accountNumber,
                    uploadOriginal
                );
            } else {
                // Otherwise just push to SharePoint and knowledgebase
                response = await sharepointService.pushToSharePointAndKnowledgebase(
                    file.fileId,
                    clientName,
                    knowledgebaseClientId,
                    selectedFolder.id,
                    selectedFolder.full_path || selectedFolder.path || '',
                    overwriteExisting, // Use checkbox state for overwrite
                    docType,
                    subType,
                    accountNumber,
                    uploadOriginal
                );
            }

            if (response.success) {
                // Call the parent callback with success info
                if (onAccept) {
                    onAccept({
                        success: true,
                        file: file,
                        sharepoint_location: response.sharepoint_location,
                        sharepoint_url: response.sharepoint_url,
                        knowledgebase_status: response.knowledgebase_status
                    });
                }
                onClose();
            } else {
                // Check if it's a file exists error
                if (response.file_exists && response.error === "FILE_EXISTS") {
                    // Show override confirmation
                    const confirmOverride = window.confirm(
                        `A file with the same name already exists in SharePoint.\n\n` +
                        `Existing file: ${response.existing_file_info?.name || 'Unknown'}\n` +
                        `Created: ${response.existing_file_info?.created_datetime ? formatTimestamp(response.existing_file_info.created_datetime) : 'Unknown'}\n\n` +
                        `Would you like to overwrite it?`
                    );

                    if (confirmOverride) {
                        let overrideResponse;
                        
                        // Retry with overwrite = true
                        if (isApprovingAll) {
                            overrideResponse = await sharepointService.approveFileAndSummary(
                                file.fileId,
                                clientName,
                                knowledgebaseClientId,
                                selectedFolderId,
                                selectedFolder.full_path || selectedFolder.path || '',
                                true, // overwrite = true
                                docType,
                                subType,
                                accountNumber,
                                uploadOriginal
                            );
                        } else {
                            overrideResponse = await sharepointService.pushToSharePointAndKnowledgebase(
                                file.fileId,
                                clientName,
                                knowledgebaseClientId,
                                selectedFolderId,
                                selectedFolder.full_path || selectedFolder.path || '',
                                true, // overwrite = true
                                docType,
                                subType,
                                accountNumber,
                                uploadOriginal
                            );
                        }

                        if (overrideResponse.success) {
                            if (onAccept) {
                                onAccept({
                                    success: true,
                                    file: file,
                                    sharepoint_location: overrideResponse.sharepoint_location,
                                    sharepoint_url: overrideResponse.sharepoint_url,
                                    knowledgebase_status: overrideResponse.knowledgebase_status,
                                    overwritten: true
                                });
                            }
                            onClose();
                        } else {
                            setError(overrideResponse.message || overrideResponse.error || 'Failed to overwrite file in SharePoint');
                        }
                    }
                    // If user doesn't want to overwrite, do nothing (stay in modal)
                } else {
                    setError(response.message || response.error || 'Failed to push file to SharePoint');
                }
            }
        } catch (err) {
            console.error('Error accepting file:', err);

            // Check if the error response contains validation errors
            let errorMessage = 'Failed to accept file';
            if (err.response?.data) {
                const errorData = err.response.data;
                if (typeof errorData === 'string') {
                    errorMessage = errorData;
                } else if (errorData.detail) {
                    // Handle FastAPI validation errors
                    if (Array.isArray(errorData.detail)) {
                        // Format validation errors properly
                        const validationErrors = errorData.detail.map(error => {
                            if (typeof error === 'object' && error.msg) {
                                return `${error.loc?.join('.')} - ${error.msg}`;
                            }
                            return error.toString();
                        });
                        errorMessage = `Validation error: ${validationErrors.join(', ')}`;
                    } else {
                        errorMessage = errorData.detail;
                    }
                } else if (errorData.message) {
                    errorMessage = errorData.message;
                } else if (errorData.error) {
                    errorMessage = errorData.error;
                }
            } else if (err.message) {
                errorMessage = err.message;
            }

            setError(errorMessage);
        } finally {
            setIsAccepting(false);
        }
    };

    const handleCreateFolder = () => {
        setShowCreateFolderModal(true);
    };

    const handleFolderCreated = async (newFolder) => {
        // Refresh the folders list to include the new folder
        await loadFolders();

        // Auto-select the newly created folder
        if (newFolder && newFolder.id) {
            setSelectedFolderId(newFolder.id);
        }

        setShowCreateFolderModal(false);
    };

    const getParentFolderPath = () => {
        // Get the parent folder path for creating new folders
        // This could be the base client path or a selected subfolder
        const selectedFolder = folders.find(f => f.id === selectedFolderId);
        return selectedFolder ? selectedFolder.full_path : `clients/${clientName}`;
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog">
                <div className="modal-dialog" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                <i className="bi bi-check-circle me-2 text-success"></i>
                                {isApprovingAll ? 'Approve All & Push' : 'Accept File'}
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={onClose}
                                disabled={isAccepting}
                            ></button>
                        </div>

                        <div className="modal-body">
                            <div className="alert alert-info">
                                <strong>
                                    {isApprovingAll 
                                        ? `This will approve the filename, push the file to SharePoint and our Knowledge base, and approve the summary for Client "${clientName}".`
                                        : `Accepting this file will push it to SharePoint and our Knowledge base for Client "${clientName}".`
                                    }
                                </strong>
                            </div>

                            {file && (
                                <div className="mb-3">
                                    <strong>File:</strong> {file.newName || file.file?.name || 'Unknown'}
                                </div>
                            )}

                            {uploadOriginal && (
                                <div className="alert alert-warning">
                                    Upload version: Original (unredacted).
                                    You will be asked to confirm before uploading.
                                </div>
                            )}

                            {fileExistsInSharePoint && (
                                <div className="alert alert-warning d-flex align-items-center justify-content-between py-2 mb-3">
                                    <div>
                                        <i className="bi bi-exclamation-triangle me-2"></i>
                                        File already exists in SharePoint
                                    </div>
                                    <div className="form-check mb-0">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="overwriteExisting"
                                            checked={overwriteExisting}
                                            onChange={(e) => setOverwriteExisting(e.target.checked)}
                                            disabled={isAccepting}
                                        />
                                        <label className="form-check-label" htmlFor="overwriteExisting">
                                            Overwrite
                                        </label>
                                    </div>
                                </div>
                            )}

                            {error && (
                                <div className="alert alert-danger">
                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                    {error}
                                </div>
                            )}

                            <div className="mb-3">
                                <label className="form-label">
                                    <strong>Select Target Folder:</strong>
                                </label>

                                {suggestedFolder && folderAutoSelected && (
                                    <div className={`alert ${folderConfidence === 'high' ? 'alert-success' : folderConfidence === 'medium' ? 'alert-info' : 'alert-warning'} py-2 mb-2`}>
                                        <small>
                                            <i className="bi bi-robot me-1"></i>
                                            <strong>AI Suggestion:</strong> {suggestedFolder.replace(/\\/g, '/')}
                                            {folderConfidence && (
                                                <span className={`badge ms-2 ${folderConfidence === 'high' ? 'bg-success' : folderConfidence === 'medium' ? 'bg-info' : 'bg-warning text-dark'}`}>
                                                    {folderConfidence} confidence
                                                </span>
                                            )}
                                        </small>
                                    </div>
                                )}

                                {loading ? (
                                    <div className="d-flex align-items-center">
                                        <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                                        Loading folders...
                                    </div>
                                ) : folders.length > 0 ? (
                                    <>
                                        <div className="d-flex align-items-center mb-2">
                                            <select
                                                className="form-select me-2"
                                                value={selectedFolderId}
                                                onChange={(e) => setSelectedFolderId(e.target.value)}
                                                disabled={isAccepting}
                                            >
                                                {folders.map((folder) => (
                                                    <option key={folder.id} value={folder.id}>
                                                        {folder.name}
                                                    </option>
                                                ))}
                                            </select>
                                            <button
                                                type="button"
                                                className="btn btn-outline-primary btn-sm"
                                                onClick={handleCreateFolder}
                                                disabled={isAccepting}
                                                title="Create new folder"
                                            >
                                                <i className="bi bi-folder-plus me-1"></i>
                                                New Folder
                                            </button>
                                        </div>

                                        {(() => {
                                            const selectedFolder = folders.find(f => f.id === selectedFolderId);
                                            return selectedFolder && (
                                                <div className="mt-2">
                                                    <small className="text-muted">
                                                        <strong>Path:</strong> {selectedFolder.full_path || selectedFolder.path}<br />
                                                    </small>
                                                </div>
                                            );
                                        })()}
                                    </>
                                ) : (
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="text-muted">
                                            No folders available.
                                            <button
                                                type="button"
                                                className="btn btn-link btn-sm p-0 ms-1"
                                                onClick={loadFolders}
                                                disabled={loading}
                                            >
                                                Retry
                                            </button>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary btn-sm"
                                            onClick={handleCreateFolder}
                                            disabled={isAccepting}
                                            title="Create new folder"
                                        >
                                            <i className="bi bi-folder-plus me-1"></i>
                                            Create First Folder
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="alert alert-secondary">
                                <i className="bi bi-info-circle me-2"></i>
                                This file will also be added to the knowledgebase for enhanced search and AI capabilities.
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={onClose}
                                disabled={isAccepting}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-success"
                                onClick={handleAccept}
                                disabled={!selectedFolderId || isAccepting || loading}
                            >
                                {isAccepting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        {isApprovingAll ? 'Approving All...' : 'Accepting...'}
                                    </>
                                ) : (
                                    <>
                                        <i className={`bi ${isApprovingAll ? 'bi-check-all' : 'bi-check-lg'} me-2`}></i>
                                        {isApprovingAll ? 'Approve All & Push' : 'Accept & Push to SharePoint'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show"></div>

            {/* Create Folder Modal */}
            <CreateFolderModal
                isOpen={showCreateFolderModal}
                onClose={() => setShowCreateFolderModal(false)}
                clientName={clientName}
                parentFolderPath={getParentFolderPath()}
                onFolderCreated={handleFolderCreated}
            />
        </>
    );
}
